import React, { useEffect, useState } from "react";
import {
  Button,
  EmailChips,
  Modal,
  fieldLabelClasses,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { doseLabel, frequencyLabel } from "../supplementMapping";
import { saveSupplementPlanPdf } from "../supplementDocument";
import { fetchSupplementPlanPdf } from "../services/supplementPlansService";
import { fetchClient } from "../../clients/services/clientsService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import type {
  PlanSupplement,
  SupplementPlanSummary,
} from "../types/supplements.types";

export interface SendSupplementModalProps {
  clientName: string;
  /**
   * The address on the client's record, which the box opens with. Absent where
   * they have none — the field then simply starts empty.
   *
   * The create screen has the client in hand already and passes it; the list
   * does not, and passes `clientId` instead so this can look it up.
   */
  clientEmail?: string;
  /**
   * Whose address to fetch, where one was not passed in. Ignored when
   * `clientEmail` is given — a record already in hand is not worth a call.
   */
  clientId?: string;
  /**
   * What is about to go — the cards with a name on them, or the supplements a
   * stored plan was read back with.
   *
   * `PlanSupplement` and not `SupplementDraft`: whether a card has been saved
   * in this sitting is the create screen's business, and this dialog is opened
   * from the list as well, over a plan that was saved some time ago.
   */
  supplements: PlanSupplement[];
  /**
   * The stored plan these supplements belong to, where there is one — what
   * Download asks the backend to render.
   *
   * Optional, and the button is absent without it: the create screen opens
   * this dialog over cards that have never been saved, and there is no
   * document to fetch for a plan that does not exist yet. Opened on a plan —
   * from the list, or from the create screen's `?plan=` — it is passed, and
   * the file it hands over is the plan as it was last stored rather than
   * whatever has since been typed into the cards behind the dialog.
   */
  plan?: SupplementPlanSummary;
  onClose: () => void;
  /**
   * Given the addresses it is going to — sending it is the caller's to do.
   *
   * Awaited: the dialog stands with its Send button in flight while the call
   * runs, and stays open if it is refused, so the addresses that were typed
   * are still there to try again with. Closing it is the caller's too, once
   * the supplements have actually gone.
   */
  onSend: (emails: string[]) => void | Promise<void>;
}

/**
 * What is about to be sent, and where it is going.
 *
 * The supplements are shown rather than asked about: everything bar the
 * address is settled by the time this opens, and reading them back is the last
 * chance to notice that one was written for the wrong client or left half
 * filled in. The same dialog the meal plan and the exercise plan are sent from
 * — see `SendPlanModal`, which is where the address field came from.
 *
 * Opened from two places: the create screen, over what it is about to write,
 * and the list's row menu, over a plan already stored.
 */
const SendSupplementModal: React.FC<SendSupplementModalProps> = ({
  clientName,
  clientEmail,
  clientId,
  supplements,
  plan,
  onClose,
  onSend,
}) => {
  const getToken = useTokenGetter();
  const toast = useToast();
  const [emails, setEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  /**
   * The client's own address, filled in from their record.
   *
   * It arrives as a chip like any other — removable, and joined by whoever
   * else this is being copied to — so the box is a starting point rather than
   * an answer. A client with none on record simply leaves it empty: an address
   * that could not be filled in is a convenience that was not there, not a
   * failure worth a message.
   */
  useEffect(() => {
    const email = clientEmail?.trim();
    if (!email) return;
    setEmails((current) => (current.length === 0 ? [email] : current));
  }, [clientEmail]);

  /**
   * And the same, for the caller that has only an id.
   *
   * The list opens this dialog over a row, which names the client and nothing
   * more about them — so the address is looked up here rather than on every
   * row of a table where most of them will never be sent.
   *
   * A lookup that fell over leaves the box empty, exactly as a client with no
   * address does: one that could not be filled in is a convenience that was
   * not there, not a failure worth a message. Anything already typed wins,
   * since the fetch may land after someone has started.
   */
  useEffect(() => {
    if (clientEmail?.trim() || !clientId) return;

    const controller = new AbortController();

    fetchClient(getToken, clientId, controller.signal)
      .then((client) => {
        const email = client.email?.trim();
        if (controller.signal.aborted || !email) return;
        setEmails((current) => (current.length === 0 ? [email] : current));
      })
      .catch(() => {});

    return () => controller.abort();
  }, [getToken, clientId, clientEmail]);

  /** What a failed request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );

  /**
   * The plan as a file. The backend renders it, so this waits on a request
   * rather than building one — hence a button that can be in flight. The same
   * document the client is emailed, and the same one the list's row menu
   * hands over.
   */
  const handleDownload = async () => {
    if (!plan) return;

    setDownloading(true);
    try {
      saveSupplementPlanPdf(
        plan,
        await fetchSupplementPlanPdf(getToken, plan.id)
      );
    } catch (reason) {
      reportFailure("Could not download the supplement plan", reason);
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(emails);
    } finally {
      // Whatever happened, the button is a button again. The dialog itself is
      // only closed by the caller, and only once they have gone.
      setSending(false);
    }
  };

  return (
    <Modal
      open
      title="Send supplements to client"
      onClose={onClose}
      className="max-w-[526px]"
      scrollBody
      footer={
        <>
          {plan && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="mr-auto rounded-md px-1 py-1 text-[14px] text-brand transition-colors hover:text-brand-dark hover:underline disabled:cursor-not-allowed disabled:text-ink-subtle disabled:no-underline"
            >
              {/* Said rather than spun: the label is the only thing in this
                  corner of the dialog, so there is room to say what is
                  happening. */}
              {downloading ? "Preparing…" : "Download"}
            </button>
          )}
          <Button
            variant="secondary"
            size="md"
            disabled={sending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            // Nowhere to send them to is not a send.
            disabled={emails.length === 0}
            loading={sending}
            onClick={handleSend}
          >
            Send
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-[110px_10px_1fr] gap-y-3 text-[14px]">
        <dt className="text-ink-muted">Client name</dt>
        <dd aria-hidden="true" className="text-ink-muted">
          :
        </dd>
        <dd className="break-words text-ink">{clientName}</dd>

        <dt className="text-ink-muted">Supplements</dt>
        <dd aria-hidden="true" className="text-ink-muted">
          :
        </dd>
        <dd className="text-ink">{supplements.length}</dd>
      </dl>

      {/* Each one named, with what it comes to — a count alone would not catch
          the supplement that was written for the wrong client. */}
      <ul className="mt-4 flex flex-col divide-y divide-line border-y border-line">
        {supplements.map((supplement) => (
          <li
            key={supplement.id}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3"
          >
            <span className="min-w-0 break-words text-[14px] text-ink">
              {supplement.name.trim()}
            </span>
            <span className="text-[13px] text-ink-muted">
              {[
                doseLabel(supplement),
                frequencyLabel({
                  frequency: supplement.frequency,
                  days: supplement.days,
                }),
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        <label className={`${fieldLabelClasses} mb-1.5 block`}>Send to</label>
        <EmailChips emails={emails} onChange={setEmails} />
      </div>
    </Modal>
  );
};

export default SendSupplementModal;
